import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  CIVILITE_OPTIONS,
  MAX_CONTACT_NAME_LENGTH,
  MAX_DEMANDE_LIBRE_LENGTH,
  MAX_EMAIL_LENGTH,
  normalizeFormData,
  resolveStepDefinition,
  stepDefinitions,
  validateField,
  validateStep,
} from "./formSchema";
import AddressAutocomplete from "./wizard/AddressAutocomplete";
import TurnstileField from "./TurnstileField";
import { surfaceRangeToNumeric } from "../domain/surfaceRanges";

export const STORAGE_KEY = "quote-wizard-state";
export const DRAFT_VERSION = 2;
export const DRAFT_TTL_MS = 24 * 60 * 60 * 1000;

const UUID_V4_PATTERN =
  /^[0-9a-f]{8}-[0-9a-f]{4}-4[0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/;

const ERROR_MESSAGES = {
  invalid_request:
    "Certaines informations sont invalides. Vérifiez le formulaire puis réessayez.",
  anti_bot_failed:
    "La vérification de sécurité a expiré. Veuillez la renouveler puis réessayer.",
  security_unavailable:
    "La vérification de sécurité est momentanément indisponible. Réessayez dans quelques instants.",
  forbidden:
    "La demande n’a pas pu être vérifiée. Rechargez la page puis réessayez.",
  conflict:
    "Cette demande ne peut pas être renvoyée avec les mêmes informations techniques. Rechargez le formulaire ou contactez NOUVAC.",
  rate_limited:
    "Trop de tentatives ont été effectuées. Patientez quelques instants avant de réessayer.",
  unavailable:
    "Le service est momentanément indisponible. Vos informations sont conservées. Réessayez dans quelques instants.",
  unknown:
    "La confirmation n’a pas pu être obtenue. Vos informations sont conservées. Réessayez sans modifier la demande.",
};

const SUCCESS_MESSAGE =
  "Votre demande a bien été reçue. Un conseiller NOUVAC reviendra vers vous pour étudier votre projet.";

function isPlainObject(value) {
  return Boolean(value) && typeof value === "object" && !Array.isArray(value);
}

export function isSubmissionId(value) {
  return typeof value === "string" && UUID_V4_PATTERN.test(value);
}

export function createSubmissionId(cryptoObject) {
  const secureCrypto =
    cryptoObject === undefined && typeof window !== "undefined"
      ? window.crypto
      : cryptoObject;

  if (typeof secureCrypto?.randomUUID === "function") {
    const value = secureCrypto.randomUUID().toLowerCase();
    if (isSubmissionId(value)) return value;
  }

  if (typeof secureCrypto?.getRandomValues === "function") {
    const bytes = new Uint8Array(16);
    secureCrypto.getRandomValues(bytes);
    bytes[6] = (bytes[6] & 0x0f) | 0x40;
    bytes[8] = (bytes[8] & 0x3f) | 0x80;
    const hex = Array.from(bytes, (byte) => byte.toString(16).padStart(2, "0")).join("");
    return [
      hex.slice(0, 8),
      hex.slice(8, 12),
      hex.slice(12, 16),
      hex.slice(16, 20),
      hex.slice(20),
    ].join("-");
  }

  throw new Error("secure_uuid_unavailable");
}

function removeStoredDraft(storage) {
  try {
    storage.removeItem(STORAGE_KEY);
  } catch {
    // A blocked storage API must not prevent the form from starting.
  }
}

function newDraft(initialUtm, uuidFactory) {
  return {
    submissionId: uuidFactory(),
    formData: normalizeFormData(initialUtm),
    needsInitialPersist: true,
  };
}

export function hydrateDraft({
  storage = window.localStorage,
  initialUtm = {},
  now = Date.now(),
  uuidFactory = createSubmissionId,
} = {}) {
  let serialized;
  try {
    serialized = storage.getItem(STORAGE_KEY);
  } catch {
    return newDraft(initialUtm, uuidFactory);
  }

  if (!serialized) return newDraft(initialUtm, uuidFactory);

  let stored;
  try {
    stored = JSON.parse(serialized);
  } catch {
    removeStoredDraft(storage);
    return newDraft(initialUtm, uuidFactory);
  }

  if (isPlainObject(stored) && stored.version === DRAFT_VERSION) {
    const savedAt = Date.parse(stored.saved_at);
    const age = now - savedAt;
    const validEnvelope =
      typeof stored.saved_at === "string" &&
      Number.isFinite(savedAt) &&
      age >= 0 &&
      age < DRAFT_TTL_MS &&
      isSubmissionId(stored.submission_id) &&
      isPlainObject(stored.form_data);

    if (validEnvelope) {
      return {
        submissionId: stored.submission_id,
        formData: normalizeFormData(stored.form_data),
        needsInitialPersist: false,
      };
    }

    removeStoredDraft(storage);
    return newDraft(initialUtm, uuidFactory);
  }

  if (isPlainObject(stored) && !hasOwnVersion(stored)) {
    return {
      submissionId: uuidFactory(),
      formData: normalizeFormData(stored),
      needsInitialPersist: true,
    };
  }

  removeStoredDraft(storage);
  return newDraft(initialUtm, uuidFactory);
}

function hasOwnVersion(value) {
  return Object.prototype.hasOwnProperty.call(value, "version");
}

function persistDraft(storage, submissionId, formData, now) {
  const envelope = {
    version: DRAFT_VERSION,
    saved_at: new Date(now).toISOString(),
    submission_id: submissionId,
    form_data: normalizeFormData(formData),
  };

  try {
    storage.setItem(STORAGE_KEY, JSON.stringify(envelope));
  } catch {
    // The current in-memory form remains usable when storage is unavailable.
  }
}

function StepContent({
  step,
  formData,
  errors,
  website,
  onWebsiteChange,
  onChange,
  onBlur,
  onProjectSelect,
  onAddressMetadataChange,
  onSurfaceSelect,
}) {
  if (step.type === "project-choice") {
    return (
      <div className="options-grid project-options-grid">
        {step.options.map((option) => {
          const isActive = formData.projet === option;
          return (
            <button
              key={option}
              type="button"
              className={`option-button ${isActive ? "active" : ""}`}
              aria-pressed={isActive}
              onClick={() => onProjectSelect(option)}
              onBlur={() => onBlur("projet")}
            >
              {option}
            </button>
          );
        })}
        {errors.projet && <div className="error">{errors.projet}</div>}
      </div>
    );
  }

  if (step.type === "service-request") {
    if (formData.projet === "Autre") {
      return (
        <FreeRequestField
          value={formData.demande_libre}
          onChange={(value) => onChange("demande_libre", value)}
          onBlur={() => onBlur("demande_libre")}
          error={errors.demande_libre}
        />
      );
    }

    return (
      <div className="options-grid service-options-grid">
        {step.options.map((option) => {
          const isActive = formData.prestations.includes(option);
          return (
            <button
              key={option}
              type="button"
              className={`option-button ${isActive ? "active" : ""}`}
              aria-pressed={isActive}
              onClick={() => {
                const next =
                  step.selectionMode === "multiple"
                    ? isActive
                      ? formData.prestations.filter((item) => item !== option)
                      : [...formData.prestations, option]
                    : [option];
                onChange("prestations", next);
              }}
              onBlur={() => onBlur("prestations")}
            >
              {option}
            </button>
          );
        })}
        {errors.prestations && <div className="error">{errors.prestations}</div>}
      </div>
    );
  }

  if (step.type === "property") {
    return (
      <div className="stack">
        {step.fields.map((field) => {
          if (field.type === "choice") {
            const labelId = `${field.id}-label`;
            return (
              <div className="field" key={field.id}>
                <span id={labelId} className="field-label">
                  {field.label} *
                </span>
                <div
                  className="options-grid"
                  role="group"
                  aria-labelledby={labelId}
                  style={{
                    gridTemplateColumns: "repeat(auto-fit, minmax(160px, 1fr))",
                  }}
                >
                  {field.options.map((option) => (
                    <button
                      key={option}
                      type="button"
                      className={`option-button ${
                        formData[field.field] === option ? "active" : ""
                      }`}
                      aria-pressed={formData[field.field] === option}
                      onClick={() => onChange(field.field, option)}
                      onBlur={() => onBlur(field.field)}
                    >
                      {option}
                    </button>
                  ))}
                </div>
                {errors[field.field] && (
                  <span className="error">{errors[field.field]}</span>
                )}
              </div>
            );
          }

          if (field.type === "address") {
            return (
              <AddressAutocomplete
                key={field.id}
                label={`${field.label} *`}
                placeholder={field.placeholder}
                value={formData[field.field]}
                onChange={(value) => onChange(field.field, value)}
                onMetadataChange={onAddressMetadataChange}
                error={errors[field.field]}
              />
            );
          }

          if (field.type === "surface-range") {
            const labelId = `${field.id}-label`;
            return (
              <div className="field" key={field.id}>
                <span id={labelId} className="field-label">
                  {field.label} *
                </span>
                <div
                  className="options-grid"
                  role="group"
                  aria-labelledby={labelId}
                  style={{
                    gridTemplateColumns: "repeat(auto-fit, minmax(160px, 1fr))",
                  }}
                >
                  {field.options.map((option) => (
                    <button
                      key={option.label}
                      type="button"
                      className={`option-button ${
                        formData[field.field]?.label === option.label ? "active" : ""
                      }`}
                      aria-pressed={formData[field.field]?.label === option.label}
                      onClick={() => onSurfaceSelect(option)}
                      onBlur={() => onBlur(field.field)}
                    >
                      {option.label}
                    </button>
                  ))}
                </div>
                {errors[field.field] && (
                  <span className="error">{errors[field.field]}</span>
                )}
              </div>
            );
          }

          return null;
        })}
      </div>
    );
  }

  if (step.type === "contact") {
    return (
      <div className="stack contact-stack">
        <div className="field">
          <span id="civilite-label" className="field-label">
            Civilité *
          </span>
          <div
            className="options-grid"
            role="group"
            aria-labelledby="civilite-label"
            style={{ gridTemplateColumns: "repeat(auto-fit, minmax(160px, 1fr))" }}
          >
            {CIVILITE_OPTIONS.map((option) => (
              <button
                key={option}
                type="button"
                className={`option-button ${
                  formData.civilite === option ? "active" : ""
                }`}
                aria-pressed={formData.civilite === option}
                onClick={() => onChange("civilite", option)}
                onBlur={() => onBlur("civilite")}
              >
                {option}
              </button>
            ))}
          </div>
          {errors.civilite && <span className="error">{errors.civilite}</span>}
        </div>
        <InputField
          id="nom"
          label="Nom *"
          value={formData.nom}
          onChange={(value) => onChange("nom", value)}
          onBlur={() => onBlur("nom")}
          error={errors.nom}
          maxLength={MAX_CONTACT_NAME_LENGTH}
          autoComplete="family-name"
          required
        />
        <InputField
          id="prenom"
          label="Prénom *"
          value={formData.prenom}
          onChange={(value) => onChange("prenom", value)}
          onBlur={() => onBlur("prenom")}
          error={errors.prenom}
          maxLength={MAX_CONTACT_NAME_LENGTH}
          autoComplete="given-name"
          required
        />
        <InputField
          id="telephone"
          label="Téléphone *"
          value={formData.telephone}
          onChange={(value) => onChange("telephone", value)}
          onBlur={() => onBlur("telephone")}
          error={errors.telephone}
          autoComplete="tel"
          required
        />
        <InputField
          id="email"
          label="E-mail *"
          value={formData.email}
          onChange={(value) => onChange("email", value)}
          onBlur={() => onBlur("email")}
          error={errors.email}
          type="email"
          maxLength={MAX_EMAIL_LENGTH}
          autoComplete="email"
          required
        />
        <div className="quote-honeypot" aria-hidden="true">
          <label htmlFor="quote-request-website">Site internet</label>
          <input
            id="quote-request-website"
            name="website"
            type="text"
            value={website}
            tabIndex={-1}
            autoComplete="new-password"
            onChange={(event) => onWebsiteChange(event.target.value)}
          />
        </div>
        <p className="privacy-notice">
          En envoyant ce formulaire, vous acceptez que NOUVAC utilise les informations
          renseignées pour traiter votre demande, conformément à notre{" "}
          <a
            href="https://nouvac.fr/pages/politique-de-confidentialite"
            target="_blank"
            rel="noopener noreferrer"
          >
            politique de confidentialité
          </a>
          .
        </p>
      </div>
    );
  }

  return null;
}

function FreeRequestField({ value, onChange, onBlur, error }) {
  const remainingCharacters = MAX_DEMANDE_LIBRE_LENGTH - value.length;
  const counterLabel = `${remainingCharacters} ${
    remainingCharacters === 1 ? "caractère restant" : "caractères restants"
  }`;

  return (
    <div className="field free-request-field">
      <label className="field-label" htmlFor="demande-libre">
        Votre demande *
      </label>
      <textarea
        id="demande-libre"
        className={`input free-request-input ${error ? "has-error" : ""}`}
        value={value}
        placeholder="Décrivez brièvement votre besoin…"
        maxLength={MAX_DEMANDE_LIBRE_LENGTH}
        required
        aria-invalid={Boolean(error)}
        aria-describedby={`demande-libre-counter${
          error ? " demande-libre-error" : ""
        }`}
        onChange={(event) =>
          onChange(event.target.value.slice(0, MAX_DEMANDE_LIBRE_LENGTH))
        }
        onBlur={onBlur}
      />
      <span
        id="demande-libre-counter"
        className="free-request-counter"
        aria-live="polite"
      >
        {counterLabel}
      </span>
      {error && (
        <span id="demande-libre-error" className="error">
          {error}
        </span>
      )}
    </div>
  );
}

function InputField({
  id,
  label,
  placeholder,
  value,
  onChange,
  onBlur,
  type = "text",
  error,
  autoComplete,
  maxLength,
  required = false,
}) {
  const errorId = id && error ? `${id}-error` : undefined;

  return (
    <label className="field">
      <span className="field-label">{label}</span>
      <input
        id={id}
        name={id}
        className={`input ${error ? "has-error" : ""}`}
        type={type}
        value={value}
        placeholder={placeholder}
        autoComplete={autoComplete}
        maxLength={maxLength}
        required={required}
        aria-invalid={Boolean(error)}
        aria-describedby={errorId}
        onChange={(event) => onChange(event.target.value)}
        onBlur={onBlur}
      />
      {error && (
        <span id={errorId} className="error">
          {error}
        </span>
      )}
    </label>
  );
}

export default function QuoteWizard({
  onSubmit,
  initialUtm = {},
  turnstileSiteKey = process.env.REACT_APP_NOUVAC_QUOTE_FORM_TURNSTILE_SITE_KEY,
  turnstileApiLoader,
  uuidFactory = createSubmissionId,
  now = Date.now,
}) {
  const [initialDraft] = useState(() =>
    hydrateDraft({
      initialUtm,
      now: now(),
      uuidFactory,
    })
  );
  const [stepIndex, setStepIndex] = useState(0);
  const [formData, setFormData] = useState(initialDraft.formData);
  const [submissionId, setSubmissionId] = useState(initialDraft.submissionId);
  const [website, setWebsite] = useState("");
  const [turnstileToken, setTurnstileToken] = useState("");
  const [errors, setErrors] = useState({});
  const [submissionState, setSubmissionState] = useState({ kind: "idle" });
  const inFlightRef = useRef(false);
  const initialSnapshotRef = useRef({
    formData: initialDraft.formData,
    submissionId: initialDraft.submissionId,
  });
  const persistenceEnabledRef = useRef(true);
  const turnstileRef = useRef(null);

  const currentStep = useMemo(
    () => resolveStepDefinition(stepDefinitions[stepIndex], formData),
    [formData, stepIndex]
  );
  const progress = useMemo(
    () => Math.round(((stepIndex + 1) / stepDefinitions.length) * 100),
    [stepIndex]
  );
  const isLastStep = stepIndex === stepDefinitions.length - 1;
  const isSubmitting = submissionState.kind === "loading";
  const shouldPersistDraft = submissionState.kind !== "success";

  useEffect(() => {
    if (!shouldPersistDraft || !persistenceEnabledRef.current) return;
    const isUntouchedHydratedDraft =
      !initialDraft.needsInitialPersist &&
      formData === initialSnapshotRef.current.formData &&
      submissionId === initialSnapshotRef.current.submissionId;
    if (isUntouchedHydratedDraft) return;
    persistDraft(window.localStorage, submissionId, formData, now());
  }, [
    formData,
    initialDraft.needsInitialPersist,
    now,
    shouldPersistDraft,
    submissionId,
  ]);

  function updateField(key, value) {
    setFormData((previous) => ({ ...previous, [key]: value }));
    if (submissionState.kind === "error") setSubmissionState({ kind: "idle" });

    setErrors((previous) => {
      const next = { ...previous };
      const validation = validateField(key, value, { ...formData, [key]: value });
      if (validation) next[key] = validation;
      else delete next[key];
      return next;
    });
  }

  function handleProjectSelection(projet) {
    if (formData.projet === projet || inFlightRef.current) return;

    setFormData((previous) => ({
      ...previous,
      projet,
      prestations: [],
      demande_libre: "",
    }));
    setSubmissionState({ kind: "idle" });
    setErrors((previous) => {
      const next = { ...previous };
      delete next.projet;
      delete next.prestations;
      delete next.demande_libre;
      return next;
    });
  }

  function handleSurfaceSelection(option) {
    const surfaceM2 = surfaceRangeToNumeric(option);
    setFormData((previous) => ({
      ...previous,
      surface_range: { ...option },
      surface_m2: surfaceM2,
    }));
    setErrors((previous) => {
      const next = { ...previous };
      delete next.surface_range;
      return next;
    });
  }

  function handleAddressMetadataChange(metadata) {
    setFormData((previous) => ({
      ...previous,
      adresse_normalisee: metadata.adresse_normalisee,
      code_postal: metadata.code_postal,
      ville: metadata.ville,
      code_insee: metadata.code_insee,
      latitude: metadata.latitude,
      longitude: metadata.longitude,
    }));
  }

  function validateFieldOnBlur(key) {
    setErrors((previous) => {
      const next = { ...previous };
      const validation = validateField(key, formData[key], formData);
      if (validation) next[key] = validation;
      else delete next[key];
      return next;
    });
  }

  async function handleFinalSubmission() {
    if (inFlightRef.current) return;
    if (!turnstileToken) {
      setSubmissionState({ kind: "error", status: "anti_bot_failed" });
      return;
    }

    let stableSubmissionId = submissionId;
    if (!isSubmissionId(stableSubmissionId)) {
      stableSubmissionId = uuidFactory();
      setSubmissionId(stableSubmissionId);
    }

    inFlightRef.current = true;
    setSubmissionState({ kind: "loading" });

    let result;
    try {
      result = await onSubmit?.({
        formData,
        submissionId: stableSubmissionId,
        turnstileToken,
        website,
      });
    } catch {
      result = { success: false, status: "unavailable", retryable: true };
    }

    turnstileRef.current?.reset();
    setTurnstileToken("");
    inFlightRef.current = false;

    if (
      result?.success === true &&
      (result.status === "created" || result.status === "exists")
    ) {
      persistenceEnabledRef.current = false;
      removeStoredDraft(window.localStorage);
      setFormData(normalizeFormData());
      setSubmissionId(uuidFactory());
      setWebsite("");
      setErrors({});
      setSubmissionState({ kind: "success", status: result.status });
      return;
    }

    const safeStatus = ERROR_MESSAGES[result?.status]
      ? result.status
      : "unavailable";
    setSubmissionState({ kind: "error", status: safeStatus });
  }

  async function handleNext() {
    if (inFlightRef.current) return;
    setSubmissionState({ kind: "idle" });
    const stepErrors = validateStep(currentStep, formData);
    setErrors(stepErrors);
    if (Object.keys(stepErrors).length) return;

    if (isLastStep) {
      await handleFinalSubmission();
      return;
    }
    setStepIndex((index) => Math.min(stepDefinitions.length - 1, index + 1));
  }

  function handleBack() {
    if (inFlightRef.current) return;
    setSubmissionState({ kind: "idle" });
    setStepIndex((index) => Math.max(0, index - 1));
  }

  const disablePrimary =
    Object.keys(validateStep(currentStep, formData)).length > 0 ||
    isSubmitting ||
    (isLastStep && (!turnstileSiteKey || !turnstileToken));

  if (submissionState.kind === "success") {
    return (
      <div className="wizard success-state" role="status">
        <p>{SUCCESS_MESSAGE}</p>
      </div>
    );
  }

  return (
    <div className="wizard">
      <div className="wizard-inner">
        <h2 className="wizard-title">
          Obtenez votre devis gratuit pour votre projet immobilier
        </h2>
        <div className="wizard-header">
          <p className="step-count">
            ÉTAPE {stepIndex + 1} / {stepDefinitions.length}
          </p>
          <h1>{currentStep.title}</h1>
          {currentStep.description ? (
            <p className="muted">{currentStep.description}</p>
          ) : null}
          <div className="progress">
            <div className="progress-bar" style={{ width: `${progress}%` }} />
          </div>
        </div>

        <div className="wizard-content">
          <div className="wizard-content-inner">
            <fieldset className="wizard-fieldset" disabled={isSubmitting}>
              <StepContent
                step={currentStep}
                formData={formData}
                errors={errors}
                website={website}
                onWebsiteChange={setWebsite}
                onChange={updateField}
                onBlur={validateFieldOnBlur}
                onProjectSelect={handleProjectSelection}
                onAddressMetadataChange={handleAddressMetadataChange}
                onSurfaceSelect={handleSurfaceSelection}
              />
            </fieldset>
            {isLastStep ? (
              <TurnstileField
                ref={turnstileRef}
                siteKey={turnstileSiteKey}
                onTokenChange={setTurnstileToken}
                loader={turnstileApiLoader}
                disabled={isSubmitting}
              />
            ) : null}
          </div>
        </div>
      </div>

      <div className="wizard-actions">
        {submissionState.kind === "loading" ? (
          <span className="loading-notice" role="status">
            Envoi de votre demande en cours…
          </span>
        ) : null}
        {submissionState.kind === "error" ? (
          <span
            className="submission-error"
            role="alert"
            data-error-status={submissionState.status}
          >
            {ERROR_MESSAGES[submissionState.status]}
          </span>
        ) : null}
        {stepIndex > 0 ? (
          <button
            type="button"
            className="secondary"
            onClick={handleBack}
            disabled={isSubmitting}
          >
            Retour
          </button>
        ) : null}
        <button
          type="button"
          className="primary"
          onClick={handleNext}
          disabled={disablePrimary}
        >
          {isLastStep
            ? isSubmitting
              ? "Envoi de votre demande…"
              : "Obtenir mon devis gratuit"
            : "Continuer"}
        </button>
      </div>
    </div>
  );
}
